import {
  Container,
  Box,
  Grid,
  Typography,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  ListItemButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import MailIcon from '@mui/icons-material/Mail';
import FacebookIcon from '@mui/icons-material/Facebook';
import XIcon from '@mui/icons-material/X';
import InstagramIcon from '@mui/icons-material/Instagram';
import { config, CONFIG } from '~/config';
import LogoWhite from './LogoWhite';
import { Stack } from '@mui/system';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { trackLinkClick } from '~/utils/analytics';

const Footer = () => {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isPhone = useMediaQuery(theme.breakpoints.down('xs'));
  const isSmallScreen = isTablet || isMobile || isPhone;

  const linkStyle = {
    color: 'inherit',
    underline: 'hover',
    fontSize: 14,
    display: 'block',
    mb: 1
  };

  const contactIconStyle = {
    color: 'secondary.main',
    bgcolor: '#fff'
  };

  const contactListItemStyle = {
    pl: 0,
  };

  const contactTextPrimaryStyle = {
    fontSize: 14,
  };

  const contactTextSecondaryStyle = {
    color: '#ccc',
    fontSize: 13,
  };

  const h4Style = {
    fontSize: 16,
    fontWeight: 700,
  };

  const getInfoLinkTrackingProps = (label: string, href: string) => ({
    target: '_blank',
    rel: 'noopener noreferrer',
    onClick: () => trackLinkClick('Footer', label, href),
  });

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: 'secondary.main',
        color: 'white',
        pb: 4,
        pt: 10,
        width: '100%', // Full width
      }}
    >
      <Container maxWidth="xl">
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ mt: -2 }}>
              <LogoWhite />
            </Box>
            <Typography sx={{ color: '#ccc' }} variant="body1">
              Your journey begins here. Tripenger connects every passenger to their perfect trip.
            </Typography>
            {
              !isSmallScreen && (
                <Box sx={{ mt: 2 }}>
                  <Stack direction="row" spacing={3}>
                    <Link href="https://www.facebook.com/tripenger/" target="_blank" sx={{ color: 'inherit' }}>
                      <FacebookIcon />
                    </Link>
                    <Link href="https://x.com/tripenger" target="_blank" sx={{ color: 'inherit' }}>
                      <XIcon />
                    </Link>
                    <Link href="https://www.instagram.com/tripenger/" target="_blank" sx={{ color: 'inherit' }}>
                      <InstagramIcon />
                    </Link>
                    <Link href="https://www.tiktok.com/@tripenger" target="_blank" sx={{ color: 'inherit', display: 'inline-flex' }} aria-label="TikTok">
                      <Box component="svg" viewBox="0 0 24 24" aria-hidden="true" sx={{ width: 24, height: 24, fill: 'currentColor' }}>
                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.3 0 .59.05.86.14V9.4a6.36 6.36 0 0 0-.86-.06A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-6.9a8.16 8.16 0 0 0 4.77 1.52V6.84c-.35 0-.7-.05-1.04-.15Z" />
                      </Box>
                    </Link>
                  </Stack>
                </Box>
              )
            }

            <Box sx={{ mt: 2 }}>
              <Typography variant="h6">Payment channels</Typography>
              <Stack direction="row" spacing={1}>
                <Link href="https://stripe.com/" target="_blank" color="inherit" underline="hover">
                  stripe |
                </Link>
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', ml: 1 }}>
                  {/* Visa Icon */}
                  <Box component="span" sx={{ mx: 0.5 }}>
                    <svg width="32" height="20" viewBox="0 0 32 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect width="32" height="20" rx="3" fill="#fff" />
                      <text x="16" y="14" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#1A1F71">VISA</text>
                    </svg>
                  </Box>
                  {/* Mastercard Icon */}
                  <Box component="span" sx={{ mx: 0.5 }}>
                    <svg width="32" height="20" viewBox="0 0 32 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect width="32" height="20" rx="3" fill="#fff" />
                      <circle cx="12" cy="10" r="6" fill="#EB001B" />
                      <circle cx="20" cy="10" r="6" fill="#F79E1B" fillOpacity="0.8" />
                    </svg>
                  </Box>
                  {/* Amex Icon */}
                  <Box component="span" sx={{ mx: 0.5 }}>
                    <svg width="32" height="20" viewBox="0 0 32 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect width="32" height="20" rx="3" fill="#fff" />
                      <text x="16" y="14" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#2E77BB">AMEX</text>
                    </svg>
                  </Box>
                </Box>
              </Stack>
            </Box>
          </Grid>

          {
            !isSmallScreen && (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography sx={h4Style} variant="h4" gutterBottom>
                    About
                  </Typography>
                  <Link href={`${config.app.helpCenterLink}/about-tripenger`} color="inherit" underline="hover" sx={linkStyle} {...getInfoLinkTrackingProps('About Tripenger', `${config.app.helpCenterLink}/about-tripenger/`)}>
                    About Tripenger
                  </Link>
                  <Link href={`${config.app.helpCenterLink}/blog`} color="inherit" underline="hover" sx={linkStyle} {...getInfoLinkTrackingProps('Tripenger Blog', `${config.app.helpCenterLink}/blog`)}>
                    Tripenger Blog
                  </Link>

                  <Link href={`${config.app.helpCenterLink}/ask-for-help`} color="inherit" underline="hover" sx={linkStyle} {...getInfoLinkTrackingProps('FAQs', `${config.app.helpCenterLink}/faqs`)}>
                    FAQs
                  </Link>
                </Grid>


                <Grid item xs={12} sm={6} md={3}>
                  <Typography sx={h4Style} variant="h4" gutterBottom>
                    Terms
                  </Typography>
                  <Link href={`${config.app.helpCenterLink}/terms-of-service`} color="inherit" underline="hover" sx={linkStyle} {...getInfoLinkTrackingProps('Terms of service', `${config.app.helpCenterLink}/terms-of-service`)}>
                    Terms of service
                  </Link>
                  <Link href={`${config.app.helpCenterLink}/privacy-policy/`} color="inherit" underline="hover" sx={linkStyle} {...getInfoLinkTrackingProps('Privacy Policy', `${config.app.helpCenterLink}/privacy-policy/`)}>
                    Privacy Policy
                  </Link>
                  <Link href={`${config.app.helpCenterLink}/cookie-policy/`} color="inherit" underline="hover" sx={linkStyle} {...getInfoLinkTrackingProps('Cookie Policy', `${config.app.helpCenterLink}/cookie-policy/`)}>
                    Cookie Policy
                  </Link>
                </Grid>
              </>
            )
          }




          <Grid item xs={12} sm={6} md={3}>
            <Typography sx={h4Style} variant="h4" gutterBottom>
              Get in touch
            </Typography>
            <List sx={{ width: '100%', maxWidth: 360, pt: 0 }}>
              <ListItem disablePadding sx={{ ...contactListItemStyle, pt: 0, mt: -1, ml: -2 }}>
                <ListItemButton component="a" href={` ${config.app.helpCenterLink}/ask-for-help/`} {...getInfoLinkTrackingProps('Customer Service', `${config.app.helpCenterLink}/ask-for-help/`)}>
                  <ListItemAvatar>
                    <Avatar sx={contactIconStyle}>
                      <SupportAgentIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Customer Service"
                    secondary="Click here for help"
                    secondaryTypographyProps={contactTextSecondaryStyle}
                    primaryTypographyProps={contactTextPrimaryStyle}
                  />
                </ListItemButton>
              </ListItem>
              <ListItem sx={contactListItemStyle}>
                <ListItemAvatar>
                  <Avatar sx={contactIconStyle}>
                    <BusinessIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary="Address"
                  secondary={CONFIG.app.addressIR}
                  secondaryTypographyProps={contactTextSecondaryStyle}
                  primaryTypographyProps={contactTextPrimaryStyle}
                />
              </ListItem>
              <ListItem sx={contactListItemStyle}>
                <ListItemAvatar>
                  <Avatar sx={contactIconStyle}>
                    <MailIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary="Email"
                  secondary={CONFIG.app.supportEmail}
                  secondaryTypographyProps={contactTextSecondaryStyle}
                  primaryTypographyProps={contactTextPrimaryStyle}
                />
              </ListItem>
            </List>
          </Grid>
        </Grid>
        <Box
          sx={{
            textAlign: 'center',
            mt: 5,
            pt: 5,
            borderTop: '#fff 1px solid',
          }}
        >
          <Typography variant="body2">&copy;2026 Tripenger Limited (773098).</Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
